import { Box, Button, Stack, Typography } from '@mui/material';
import { Uppy } from '@uppy/core';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useRef, useState } from 'react';

import { sendDatadogRumError } from 'utils/analytics/datadogRum';
import FileArrowUpIcon from 'assets/icons/FileArrowUp.svg';
import UploadedFileRow from 'modules/common/components/UploadedFileRow';
import UppyUploader from 'modules/common/components/UppyUploader/UppyUploader';
import { Document } from 'modules/common/interfaces/documents';
import { useMember } from 'modules/common/queries/member';

export interface ExistingDocument {
    id: string;
    file_name: string;
    file_size?: number;
}

export interface StagedFile {
    id: string;
    name: string;
    size: number;
}

interface FileUploadAreaProps {
    /** Document type for backend classification */
    documentType: string;
    /** Primary drop-zone heading. Defaults to "Drag and drop your file". */
    label?: string;
    /** Additional metadata to attach to uploads */
    meta?: Record<string, any>;
    /** Optional custom upload endpoint (for relay uploads) */
    uploadEndpoint?: string;
    /** Called when files are successfully uploaded */
    onUploadSuccess?: (uploadedFiles: Document[]) => void;
    /** Called when upload fails */
    onUploadError?: (error: Error) => void;
    /** Called when uploading state changes */
    onUploadingChange?: (isUploading: boolean) => void;
    /** Allowed file types */
    accept?: string;
    /** Hide the uploaded files list section */
    hideUploadedFiles?: boolean;
    /** Existing documents to display (already uploaded to backend) */
    existingDocuments?: ExistingDocument[];
    /** Called when an existing document is deleted */
    onDeleteExistingDocument?: (docId: string) => void;
    /** If true, files are staged locally and not uploaded until uploadTrigger changes */
    deferUpload?: boolean;
    /** Increment this number to trigger upload of staged files (only when deferUpload=true) */
    uploadTrigger?: number;
    /** Called when deferred upload completes with the uploaded documents */
    onUploadComplete?: (docs: Document[]) => void;
    /** Called when staged files change (only when deferUpload=true) */
    onStagedFilesChange?: (files: StagedFile[]) => void;
}

/**
 * Reusable file upload area with drag-drop, Uppy integration, and uploaded files list.
 * Handles the complete upload flow from file selection to backend upload.
 */
function FileUploadArea({
    documentType,
    label = 'Drag and drop your file',
    meta = {},
    uploadEndpoint = undefined,
    onUploadSuccess = undefined,
    onUploadError = undefined,
    onUploadingChange = undefined,
    accept = '.pdf,.docx,.xlsx,.csv,.jpg,.jpeg,.png',
    hideUploadedFiles = false,
    existingDocuments = [],
    onDeleteExistingDocument = undefined,
    deferUpload = false,
    uploadTrigger = 0,
    onUploadComplete = undefined,
    onStagedFilesChange = undefined,
}: FileUploadAreaProps) {
    const { data: member } = useMember();
    const { enqueueSnackbar } = useSnackbar();
    const [uploadedFiles, setUploadedFiles] = useState<Document[]>([]);
    const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
    const uppyRef = useRef<Uppy | null>(null);
    const isRelayUpload = Boolean(uploadEndpoint);

    // Track the last processed trigger to avoid re-triggering
    // Initialize with uploadTrigger so remounts don't trigger stale values
    const lastTriggerRef = useRef(uploadTrigger);

    // Watch for uploadTrigger changes to initiate deferred upload
    useEffect(() => {
        if (!deferUpload || uploadTrigger === 0 || uploadTrigger === lastTriggerRef.current) {
            return;
        }

        lastTriggerRef.current = uploadTrigger;

        if (!uppyRef.current) {
            onUploadComplete?.([]);
            return;
        }

        const files = uppyRef.current.getFiles();
        if (files.length === 0) {
            onUploadComplete?.([]);
            return;
        }

        const handleComplete = (result: any) => {
            const uploadedDocs = (result.successful || [])
                .map((file: any) => file.response?.body as Document)
                .filter(Boolean);
            const failedCount = result.failed?.length || 0;

            // If all uploads failed, keep staged files so user can retry
            // Error handling is done by handleUploadError via UppyUploader
            if (uploadedDocs.length === 0) {
                return;
            }

            // Clear staged files only after successful uploads
            setStagedFiles([]);
            onStagedFilesChange?.([]);

            // Clear Uppy instance files
            uppyRef.current?.getFiles().forEach((file) => {
                uppyRef.current?.removeFile(file.id);
            });

            // If some succeeded but some failed, show partial success
            if (failedCount > 0) {
                enqueueSnackbar(`${uploadedDocs.length} document(s) uploaded, ${failedCount} failed`, {
                    variant: 'warning',
                    autoHideDuration: 5000,
                });
            } else {
                enqueueSnackbar(
                    uploadedDocs.length > 1
                        ? `${uploadedDocs.length} documents uploaded successfully!`
                        : 'Document uploaded successfully!',
                    { variant: 'success', autoHideDuration: 3000 }
                );
            }

            onUploadComplete?.(uploadedDocs);
        };

        uppyRef.current.once('complete', handleComplete);
        uppyRef.current.upload();

        // Cleanup on unmount
        return () => {
            uppyRef.current?.off('complete', handleComplete);
        };
    }, [uploadTrigger, deferUpload, onUploadComplete, onStagedFilesChange, enqueueSnackbar]);

    const handleDeleteFile = useCallback(
        (fileId: string) => {
            setUploadedFiles((prev) => {
                const updated = prev.filter((file: any) => {
                    const fileObj = Array.isArray(file) ? file[0] : file;
                    return fileObj.id !== fileId;
                });
                onUploadSuccess?.(updated);
                return updated;
            });

            enqueueSnackbar('File deleted successfully!', {
                variant: 'success',
                autoHideDuration: 3000,
            });
        },
        [enqueueSnackbar, onUploadSuccess]
    );

    const handleUploadSuccess = useCallback(
        (uploadedDocuments: Document[]) => {
            // This callback is used for non-deferred (immediate) uploads only
            // Deferred uploads are handled by the uploadTrigger useEffect
            if (deferUpload) {
                return;
            }

            onUploadingChange?.(false);

            const newFiles = Array.isArray(uploadedDocuments) ? uploadedDocuments : [uploadedDocuments];
            setUploadedFiles((prev) => {
                const updated = [...prev, ...newFiles];
                onUploadSuccess?.(updated);
                return updated;
            });

            // Clear Uppy instance files
            if (uppyRef.current) {
                uppyRef.current.getFiles().forEach((file) => {
                    uppyRef.current?.removeFile(file.id);
                });
            }

            enqueueSnackbar('Document uploaded successfully!', {
                variant: 'success',
                autoHideDuration: 3000,
            });
        },
        [deferUpload, enqueueSnackbar, onUploadSuccess, onUploadingChange]
    );

    const handleUploadError = useCallback(
        (error: Error) => {
            onUploadingChange?.(false);

            enqueueSnackbar(`Upload failed: ${error.message || 'Unknown error occurred'}`, {
                variant: 'error',
                autoHideDuration: 5000,
            });

            onUploadError?.(error);
        },
        [enqueueSnackbar, onUploadError, onUploadingChange]
    );

    const handleFileAdded = useCallback(() => {
        if (deferUpload) {
            // In deferred mode, track staged files for display
            const files = uppyRef.current?.getFiles() || [];
            const newStagedFiles = files.map((f) => ({ id: f.id, name: f.name || 'Unnamed file', size: f.size || 0 }));
            setStagedFiles(newStagedFiles);
            onStagedFilesChange?.(newStagedFiles);
        } else {
            // In immediate mode, trigger upload right away
            onUploadingChange?.(true);
            // Use queueMicrotask instead of setTimeout to avoid race conditions
            queueMicrotask(() => {
                uppyRef.current?.upload?.();
            });
        }
    }, [deferUpload, onUploadingChange, onStagedFilesChange]);

    const handleFileRemoved = useCallback(() => {
        onUploadingChange?.(false);
        if (deferUpload) {
            // Update staged files from Uppy's current state
            const files = uppyRef.current?.getFiles() || [];
            const newStagedFiles = files.map((f) => ({ id: f.id, name: f.name || 'Unnamed file', size: f.size || 0 }));
            setStagedFiles(newStagedFiles);
            onStagedFilesChange?.(newStagedFiles);
        }
    }, [deferUpload, onUploadingChange, onStagedFilesChange]);

    const handleDeleteStagedFile = useCallback(
        (fileId: string) => {
            if (uppyRef.current) {
                uppyRef.current.removeFile(fileId);
                const files = uppyRef.current.getFiles();
                const newStagedFiles = files.map((f) => ({
                    id: f.id,
                    name: f.name || 'Unnamed file',
                    size: f.size || 0,
                }));
                setStagedFiles(newStagedFiles);
                onStagedFilesChange?.(newStagedFiles);
            }
        },
        [onStagedFilesChange]
    );

    const addFileToUppy = useCallback(
        (file: File) => {
            if (!uppyRef.current) {
                return;
            }

            try {
                uppyRef.current.addFile({
                    name: file.name,
                    type: file.type,
                    data: file,
                    meta: {
                        document_type: documentType,
                        ...meta,
                    },
                });
                // Upload is triggered by handleFileAdded callback via Uppy's file-added event
            } catch (error) {
                const fileAddError = error instanceof Error ? error : new Error('Unknown error adding file');
                sendDatadogRumError('FileUploadArea.addFileToUppy', fileAddError, {
                    source: 'FileUploadArea.addFileToUppy',
                    documentType,
                    hasFileName: !!file.name,
                    fileType: file.type,
                    fileSize: file.size,
                    hasUploadEndpoint: !!uploadEndpoint,
                });
                enqueueSnackbar(fileAddError.message || 'Error adding file. Please try again.', {
                    variant: 'error',
                    autoHideDuration: 5000,
                });
                onUploadError?.(fileAddError);
            }
        },
        [documentType, meta, enqueueSnackbar, uploadEndpoint, onUploadError]
    );

    const handleDrop = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            const files = Array.from(e.dataTransfer.files);
            if (files.length > 0 && uppyRef.current) {
                files.forEach(addFileToUppy);
            }
        },
        [addFileToUppy]
    );

    const handleClick = useCallback(() => {
        const input = window.document.createElement('input');
        input.type = 'file';
        input.multiple = true;
        input.accept = accept;
        input.style.display = 'none';
        window.document.body.appendChild(input);

        input.onchange = () => {
            if (input.files && input.files.length > 0 && uppyRef.current) {
                Array.from(input.files).forEach(addFileToUppy);
            }
            window.document.body.removeChild(input);
        };

        input.click();
    }, [accept, addFileToUppy]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Upload Area */}
            <Box
                onClick={handleClick}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                onDragEnter={(e) => e.preventDefault()}
                sx={{
                    p: 3,
                    backgroundColor: 'cement.main',
                    borderRadius: 2,
                    border: 2,
                    borderColor: 'cement.300',
                    borderStyle: 'dashed',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        backgroundColor: 'cement.50',
                        borderColor: 'cement.400',
                        boxShadow: 1,
                    },
                }}>
                <Stack alignItems="center" spacing={7}>
                    <Stack alignItems="center" spacing={2}>
                        {/* Upload Icon */}
                        <Stack p={2}>
                            <img src={FileArrowUpIcon} alt="Upload file" style={{ width: 32, height: 32 }} />
                        </Stack>

                        {/* Upload Text */}
                        <Stack alignItems="center" spacing={0.25} textAlign="center">
                            <Typography variant="body1" color="common.black">
                                {label}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Supported files: {accept}
                            </Typography>
                        </Stack>

                        {/* Upload Button */}
                        <Button variant="outlined" color="secondary" sx={{ minWidth: 120, textTransform: 'none' }}>
                            Upload
                        </Button>
                    </Stack>
                </Stack>

                {/* Hidden UppyUploader */}
                <Box display="none">
                    <UppyUploader
                        ref={uppyRef}
                        documentType={documentType}
                        memberUUID={member?.uuid}
                        xhrOptions={
                            isRelayUpload && uploadEndpoint ? { endpoint: uploadEndpoint, method: 'PUT' } : undefined
                        }
                        meta={{
                            document_type: documentType,
                            ...meta,
                        }}
                        onUploadSuccess={handleUploadSuccess}
                        onUploadError={handleUploadError}
                        onFileAdded={handleFileAdded}
                        onFileRemoved={handleFileRemoved}
                        hideUploadButton
                        shouldPreloadData={false}
                        options={{
                            restrictions: {
                                maxFileSize: 100 * 1024 * 1024, // 100MB
                                allowedFileTypes: accept.split(',').map((type) => type.trim()),
                            },
                        }}
                    />
                </Box>
            </Box>
            {/* File List - Existing Documents */}
            {existingDocuments.length > 0 && (
                <Stack>
                    {existingDocuments.map((doc, index) => {
                        const isFirst = index === 0;
                        const isLast = index === existingDocuments.length - 1;

                        return (
                            <UploadedFileRow
                                key={doc.id}
                                name={doc.file_name}
                                sizeKB={doc.file_size ? doc.file_size / 1024 : undefined}
                                onDelete={onDeleteExistingDocument ? () => onDeleteExistingDocument(doc.id) : () => {}}
                                isFirst={isFirst}
                                isLast={isLast}
                            />
                        );
                    })}
                </Stack>
            )}
            {/* File List - Staged Files (deferred upload mode) */}
            {deferUpload && stagedFiles.length > 0 && (
                <Stack>
                    {stagedFiles.map((file, index) => {
                        const isFirst = index === 0 && existingDocuments.length === 0;
                        const isLast = index === stagedFiles.length - 1;

                        return (
                            <UploadedFileRow
                                key={file.id}
                                name={file.name}
                                sizeBytes={file.size}
                                onDelete={() => handleDeleteStagedFile(file.id)}
                                isFirst={isFirst}
                                isLast={isLast}
                            />
                        );
                    })}
                </Stack>
            )}
            {/* File List - Newly Uploaded Files */}
            {!hideUploadedFiles && uploadedFiles.length > 0 && (
                <Stack>
                    {uploadedFiles.map((file, index) => {
                        const fileObj = Array.isArray(file) ? file[0] : file;
                        const isFirst = index === 0 && existingDocuments.length === 0;
                        const isLast = index === uploadedFiles.length - 1;

                        return (
                            <UploadedFileRow
                                key={fileObj.id || index}
                                name={fileObj.file_name || fileObj.name || 'Uploaded file'}
                                sizeKB={fileObj.file_size ? fileObj.file_size / 1024 : undefined}
                                onDelete={() => handleDeleteFile(fileObj.id)}
                                isFirst={isFirst}
                                isLast={isLast}
                            />
                        );
                    })}
                </Stack>
            )}
        </Box>
    );
}

export default FileUploadArea;
